import { NavigateFunction } from "react-router-dom";
import { TFunction } from "i18next";
import { CardListItemImage, CardListItemProps } from "/src/components/card-home/CardList";
import { ActionCardListItemProps } from "/src/components/card-home/ActionCardListItem";
import { getBusinessName } from "/src/util/card-v2";
import { Card, CardStatus } from "/src/protos/squareup/bbdebit/cardmanagement";
import { CardCustomization } from "/src/protos/squareup/bizbank/service";
import { getCardSignatureUri } from "/src/util/card-v2";

export const createActivatableCardList =
  (t: TFunction) =>
  (
    cards: Card[],
    customizationMap: Map<string, CardCustomization | null>,
    activationCallback: (card: Card) => void
  ): ActionCardListItemProps[] =>
    cards.map((card) =>
      createActivatableCardListItem(
        card,
        customizationMap.get(card.id) ?? null,
        t,
        activationCallback
      )
    );

function createActivatableCardListItem(
  card: Card,
  customization: CardCustomization | null,
  t: TFunction,
  activationCallback: (card: Card) => void
): ActionCardListItemProps {
  const cardImage = getCardListItemImage(card, customization ?? undefined);

  return {
    buttonText: t("cardList.existingCard.activateCard.buttonText"),
    cardImage,
    cardToken: card.id,
    subtext: t("cardList.existingCard.lastFourPanText", {
      replace: {
        lastFourPan: card.lastFour,
      },
    }),
    title: cardImage.businessName,
    onItemClick: () => activationCallback(card),
  };
}

export const createCardList =
  (navigate: NavigateFunction, t: TFunction) =>
  (cards: Card[], customizationMap: Map<string, CardCustomization | null>): CardListItemProps[] =>
    cards.map((card) => {
      const customization = customizationMap.get(card.id) ?? null;
      return createCardListItem(card, customization, navigate, t);
    });

function createCardListItem(
  card: Card,
  customization: CardCustomization | null,
  navigate: NavigateFunction,
  t: TFunction
): CardListItemProps {
  const cardImage = getCardListItemImage(card, customization ?? undefined);

  return {
    cardImage,
    cardToken: card.id,
    subtext: t("cardList.existingCard.lastFourPanText", {
      replace: {
        lastFourPan: card.lastFour,
      },
    }),
    title: cardImage.businessName,
    onItemClick: () => navigate(`/card/${card.id}/settings`),
  };
}

export const getCardListItemImage = (
  card: Card,
  customization?: CardCustomization
): CardListItemImage => ({
  businessName: getBusinessName(card),
  signatureUri: getCardSignatureUri(customization),
});

/**
 * Sort cards into two arrays, activatable cards and claimed cards
 */
export const sortCards = (cards: Card[]): [Card[], Card[]] =>
  cards.reduce<[Card[], Card[]]>(
    ([accActivatableCards, accClaimedCards], card) => {
      if (card.status === CardStatus.CARD_STATUS_ACTIVATABLE) {
        accActivatableCards.push(card);
      } else {
        accClaimedCards.push(card);
      }
      return [accActivatableCards, accClaimedCards];
    },
    [[], []]
  );
